import './main.css'
import React, { forwardRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import concatWithSpaces from 'components/ProjectNonSpecificComponents/Functions/concatWithSpaces'

interface Props extends React.ComponentProps<'div'> {
  top?: string | number
  left?: string | number
  bottom?: string | number
  right?: string | number
  /**
   * Default `z-index` is 100.
   */
  zIndex?: number
  /**
   * If true, makes the overlay center its content
   * in the center of the screen
   */
  screenCentered?: boolean
  entranceFliesUpAnimated?: boolean
  hideOverlayWithFadeout?: boolean
  /**
   * Hide fadeout duration in milliseconds
   */
  fadeoutDuration?: number
  onFadeoutFinish?: () => void
  children: React.ReactNode
}

/**
 * Similar to `<HyperDiv />` — provides many functionalities
 * in the realm of overlays.
 * 
 * 
 * #### `screenCentered`
 * Centers content on screen.
 * 
 * #### `entranceFliesUpAnimated`
 * Flies upward on first render.
 * 
 * By default is 1s.
 * 
 * #### `hideOverlayWithFadeout`
 * Hides overlay with a fadeout effect.
 * 
 * Relevant props: `onFadeoutFinish`, `fadeoutDuration`.
 */
const Overlay = forwardRef<HTMLDivElement, Props>(
  ({
    top,
    left,
    bottom,
    right,
    zIndex=100,
    screenCentered=false,
    entranceFliesUpAnimated=false,
    hideOverlayWithFadeout=false,
    fadeoutDuration=1000,
    onFadeoutFinish=() => {},
    style,
    className,
    children,
    ...props
  }, ref) => {
    const isEntranceAnimated = entranceFliesUpAnimated
    const [isOverlayHidden, setIsOverlayHidden] = useState(isEntranceAnimated)
    const [isFadeProcessActive, setIsFadeProcessActive] = useState(false)

    useEffect(() => {
      // On initial render, run the show animation
      setIsOverlayHidden(false)
    }, [])
  
    useEffect(() => {
      if (!hideOverlayWithFadeout) {
        setIsOverlayHidden(false)
        return
      }
  
      setIsFadeProcessActive(true)
      let timeoutId = setTimeout(() => {
        setIsOverlayHidden(true)
        setIsFadeProcessActive(false)
        onFadeoutFinish()
      }, fadeoutDuration)
      
      return () => {
        clearTimeout(timeoutId)
        setIsFadeProcessActive(false)
        // onFadeoutCancel()
      }
    }, [hideOverlayWithFadeout])

    // Set `top` and/or `left` to 0,
    // if the opposite positional value has not been provided,
    // and if no value has been provided for itself.
    // Also account for positional values in classes.
    if (!top && !bottom && !screenCentered) top = 0
    if (!left && !right && !screenCentered) left = 0

    return (
      <CustomOverlay
        {...props}
        className={concatWithSpaces(
          screenCentered ? 'screen-centered' : '',
          className,
        )}
        style={{
          top,
          left,
          bottom,
          right,
          zIndex,
          ...style,
        }}
        ref={ref}
      >
        <AnimatedBase
          className={entranceFliesUpAnimated ? 'initial-state-flies-up-entrance' : ''}
          style={{
            ...(!isOverlayHidden ? {
              opacity: 1,
              transform: 'translateY(0%)',
            } : {}),
            ...(isFadeProcessActive ? {
              opacity: 0,
              transition: `opacity ${fadeoutDuration}ms linear`,
            } : {}),
          }}
        >
          {children}
        </AnimatedBase>
      </CustomOverlay>
    )
  }
)

export default Overlay

const CustomOverlay = styled.div`
  position: fixed;
`

const AnimatedBase = styled.div`
  // Animation
  transition: 0.2s var(--default-slide-transition-fn);
  transition-property: opacity, transform;
`
